// Axios settings
import Cookies from 'js-cookie';
import axiosOrigin from 'axios';
import * as axiosConfig from './axios.config.lib';
import * as navigator from '@/lib/navigator';
import { onHttpError } from './lib/error_handling';
import { mockLogDisplayer } from '@/mocks/mockLogDisplayer';
import { captureAuthorizationError, captureHttpError } from '@/sentry.config';

import NodeCache from 'node-cache';
const myCache = new NodeCache({ stdTTL: 60 * 60 * 2, checkperiod: 120 });

export function hasBaseUrlConfigInCookies() {
  if (navigator.appEnv !== 'prod' && navigator.appEnv !== 'next') {
    return true;
  }
  return false;
}

export function baseUrlForService(serviceType) {
  if (hasBaseUrlConfigInCookies()) {
    const domainTypeFromCookie = Cookies.get('app_type');
    const apiTypeFromCookie = Cookies.get('api_type');

    const domainType = domainTypeFromCookie ? domainTypeFromCookie : navigator.appDomain;
    const apiEnv = apiTypeFromCookie ? apiTypeFromCookie : navigator.appEnv;

    return axiosConfig.apiUrl(apiEnv, domainType, serviceType);
  }
  return axiosConfig.apiUrl(navigator.appEnv, navigator.appDomain, serviceType);
}

/**
 * Login API
 */
export const loginUrl = baseUrlForService('login');
export const loginApi = axiosOrigin.create({
  baseURL: loginUrl,
  headers: { 'Content-Type': 'multipart/form-data' },
});

loginApi.interceptors.request.use(
  function(config) {
    delete config.headers.common['Authorization'];
    if (hasBaseUrlConfigInCookies()) {
      config.baseURL = baseUrlForService('login');
    }
    return config;
  },
  function(error) {
    return Promise.reject(onHttpError(error));
  }
);

loginApi.interceptors.response.use(
  function(response) {
    mockLogDisplayer('response', response);

    return response;
  },
  function(error) {
    if (error.response) {
      captureAuthorizationError(error.response);
    }

    return Promise.reject(error);
  }
);

/**
 * Base API
 */
export const baseUrl = baseUrlForService('basic');
export const axios = axiosOrigin.create({
  baseURL: baseUrl,
});

axios.interceptors.request.use(
  function(config) {
    if (hasBaseUrlConfigInCookies()) {
      config.baseURL = baseUrlForService('basic');
    }
    return config;
  },
  function(error) {
    return Promise.reject(onHttpError(error));
  }
);

axios.interceptors.response.use(
  function(response) {
    mockLogDisplayer('response', response);

    return response;
  },
  function(error) {
    if (error.response) {
      captureHttpError(error.response);
    }

    return Promise.reject(error);
  }
);

/**
 * Blue API
 */
export const blueUrl = baseUrlForService('blue');
export const blueApi = axiosOrigin.create({
  baseURL: blueUrl,
});
blueApi.CancelToken = axiosOrigin.CancelToken;
blueApi.isCancel = axiosOrigin.isCancel;

blueApi.interceptors.request.use(
  function(config) {
    if (hasBaseUrlConfigInCookies()) {
      config.baseURL = baseUrlForService('blue');
    }
    return config;
  },
  function(error) {
    return Promise.reject(onHttpError(error));
  }
);

blueApi.interceptors.response.use(
  function(response) {
    mockLogDisplayer('response', response);

    return response;
  },
  function(error) {
    if (error.response) {
      captureHttpError(error.response);
    }

    // return Promise.reject(error);
    return Promise.reject(onHttpError(error));
  }
);

/**
 * Auth API
 */
export const authUrl = baseUrlForService('auth');
export const authApi = axiosOrigin.create({
  baseURL: authUrl,
});

authApi.interceptors.request.use(
  function(config) {
    if (hasBaseUrlConfigInCookies()) {
      config.baseURL = baseUrlForService('auth');
    }
    return config;
  },
  function(error) {
    return Promise.reject(onHttpError(error));
  }
);

authApi.interceptors.response.use(
  function(response) {
    mockLogDisplayer('response', response);

    return response;
  },
  function(error) {
    if (error.response) {
      captureHttpError(error.response);
    }

    return Promise.reject(onHttpError(error));
  }
);

/**
 * Status API
 */
export const statusUrl = baseUrlForService('status');
export const statusApi = axiosOrigin.create({
  baseURL: statusUrl,
});

axios.CancelToken = axiosOrigin.CancelToken;
axios.isCancel = axiosOrigin.isCancel;

statusApi.interceptors.request.use(
  function(config) {
    if (hasBaseUrlConfigInCookies()) {
      config.baseURL = baseUrlForService('status');
    }
    return config;
  },
  function(error) {
    return Promise.reject(onHttpError(error));
  }
);

statusApi.interceptors.response.use(
  function(response) {
    mockLogDisplayer('response', response);

    return response;
  },
  function(error) {
    if (error.response) {
      captureHttpError(error.response);
    }

    return Promise.reject(onHttpError(error));
  }
);

/**
 * User clients API
 */
export const userUrl = baseUrlForService('user');
export const userApi = axiosOrigin.create({
  baseURL: userUrl,
});

userApi.interceptors.request.use(
  function(config) {
    if (hasBaseUrlConfigInCookies()) {
      config.baseURL = baseUrlForService('user');
    }
    return config;
  },
  function(error) {
    return Promise.reject(onHttpError(error));
  }
);

userApi.interceptors.response.use(
  function(response) {
    mockLogDisplayer('response', response);

    return response;
  },
  function(error) {
    if (error.response) {
      captureHttpError(error.response);
    }

    return Promise.reject(onHttpError(error));
  }
);

/**
 * MFA API
 */
export const mfaUrl = baseUrlForService('mfa');
export const mfaApi = axiosOrigin.create({
  baseURL: mfaUrl,
});

mfaApi.interceptors.request.use(
  function(config) {
    if (hasBaseUrlConfigInCookies()) {
      config.baseURL = baseUrlForService('mfa');
    }
    return config;
  },
  function(error) {
    return Promise.reject(onHttpError(error));
  }
);

mfaApi.interceptors.response.use(
  function(response) {
    return response;
  },
  function(error) {
    if (error.response) {
      captureHttpError(error.response);
    }

    return Promise.reject(onHttpError(error));
  }
);

export const cached = myCache;
